import { API_ENDPOINTS } from 'Constants/env';
import { ConfigurationResponse } from 'Interfaces/apiDtos';
import { AxiosResponseT } from 'Interfaces/axiosResponse';
import { action, observable, makeObservable, runInAction } from 'mobx';
import { fromPromise, IPromiseBasedObservable } from 'mobx-utils';
import { getLogger, Logger } from 'Utils/logger';
import { bugsnagClient } from 'Utils/logUtils';
import { SipConnectionRecoveryService } from '~/services/sipConnectionRecoveryService';
import API from '../api';
import { BaseStore } from './BaseStore';
import { RootStore } from './RootStore';

/*
    Because user config settings that come from the server needs to be used
    in so many places, and because they are mostly static but may need
    to be observed, we are allowing in this special case the ability
    to inject the ConfigStore into any component at any level. Note that
    this is against our standard practices.
*/
export class ConfigStore extends BaseStore {
  private recoveryService?: SipConnectionRecoveryService;
  private logger: Logger;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this);
    this.logger = getLogger();
  }

  @action
  clearAllData = () => {
    this.signedInPersonConfig = null;
    this.recoveryService?.stopRecovery();
  };

  @observable public signedInPersonConfig: IPromiseBasedObservable<
    AxiosResponseT<ConfigurationResponse>
  > = null;

  @observable public signedInPersonConfigLoaded = false;

  @action
  loadConfig = async () => {
    bugsnagClient.leaveBreadcrumb('CUU2-2648 - Loading config');

    try {
      this.signedInPersonConfig = fromPromise(
        API.get<ConfigurationResponse>(API_ENDPOINTS.Config)
      );

      const config = await this.signedInPersonConfig;

      bugsnagClient.leaveBreadcrumb(
        'CUU2-2648 - Load config successful',
        config
      );

      runInAction(() => {
        this.signedInPersonConfigLoaded = true;
      });

      return config.data;
    } catch (error) {
      bugsnagClient.leaveBreadcrumb(
        'CUU2-2648 - Load config failed',
        error as Error
      );

      // Start recovery process when config loading fails
      void this.startConfigRecovery();
    }

    return null;
  };

  /**
   * Initializes the recovery service if not already created
   */
  private initializeRecoveryService(): void {
    if (!this.recoveryService) {
      this.recoveryService = new SipConnectionRecoveryService(this.rootStore, {
        initialDelay: 500,
        maxDelay: 16000,
        maxAttempts: 10,
        enableConsoleLogging: true,
        timeout: 20000,
      });
    }
  }

  /**
   * Starts the config recovery process with automatic SIP reconnection
   */
  @action
  startConfigRecovery = async (): Promise<void> => {
    this.initializeRecoveryService();

    try {
      await this.recoveryService.startConfigRecovery();
    } catch (error) {
      this.logger.error(
        'ConfigStore startConfigRecovery failed',
        error as Error
      );
    }
  };

  /**
   * Exposes recovery service state for UI components
   */
  get recoveryState() {
    return this.recoveryService
      ? {
          isRecovering: this.recoveryService.isRecovering,
          lastAttempt: this.recoveryService.lastRecoveryAttempt,
          attemptCount: this.recoveryService.recoveryAttemptCount,
        }
      : {
          isRecovering: false,
          lastAttempt: null,
          attemptCount: 0,
        };
  }
}
export default ConfigStore;
